/**
 * GeoJSON polygon (lng/lat coordinates).
 */
export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

/**
 * GeoJSON line string (lng/lat coordinates).
 */
export interface GeoJsonLineString {
  type: 'LineString';
  coordinates: number[][];
}

const METERS_PER_DEG_LAT = 111_320;

function metersPerDegLng(lat: number): number {
  return METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
}

/**
 * Project a lng/lat coordinate to local meters relative to a center point.
 * X = east (meters), Z = -north (meters, negated for three.js convention).
 */
export function projectLngLat(
  lng: number,
  lat: number,
  centerLng: number,
  centerLat: number
): [number, number] {
  const x = (lng - centerLng) * metersPerDegLng(centerLat);
  const z = -(lat - centerLat) * METERS_PER_DEG_LAT;
  return [x, z];
}

/**
 * Project a GeoJSON polygon's outer ring to local [x, z] coordinates.
 */
export function projectPolygon(
  polygon: GeoJsonPolygon,
  centerLng: number,
  centerLat: number
): [number, number][] {
  const ring = polygon.coordinates[0]!;
  return ring.map(([lng, lat]) => projectLngLat(lng!, lat!, centerLng, centerLat));
}

/**
 * Project a GeoJSON line string to local [x, z] coordinates.
 */
export function projectLineString(
  line: GeoJsonLineString,
  centerLng: number,
  centerLat: number
): [number, number][] {
  return line.coordinates.map(([lng, lat]) => projectLngLat(lng!, lat!, centerLng, centerLat));
}

// ── Status colors for issue beacons ────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  submitted: '#ef4444',      // red
  acknowledged: '#f97316',   // orange
  in_progress: '#3b82f6',    // blue
  scheduled: '#6366f1',      // indigo
  resolved: '#22c55e',       // green
  closed: '#64748b',         // slate
  rejected: '#94a3b8',       // light slate
  duplicate: '#a855f7'       // purple
};

export const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  scheduled: 'Scheduled',
  resolved: 'Resolved',
  closed: 'Closed',
  rejected: 'Rejected',
  duplicate: 'Duplicate'
};

export function statusGroup(status: string): 'open' | 'in_progress' | 'resolved' {
  if (status === 'submitted' || status === 'acknowledged') return 'open';
  if (status === 'in_progress' || status === 'scheduled') return 'in_progress';
  return 'resolved';
}

// ── Asset type shapes/colors ───────────────────────────────────────────────

export const ASSET_TYPE_COLORS: Record<string, string> = {
  stop_sign: '#dc2626',
  streetlight: '#fbbf24',
  garbage_bin: '#16a34a',
  recycling_bin: '#0891b2',
  storm_drain: '#4b5563',
  fire_hydrant: '#dc2626',
  utility_pole: '#92400e',
  park_bench: '#854d0e',
  traffic_light: '#dc2626',
  bus_stop_sign: '#2563eb'
};
